/**
 * App Access Utilities
 * Centralized logic for determining app access permissions and public viewer status
 */

import {
  getAppType,
  canRunAppTypeInEnvironment,
  appTypeSupportsWorkspace,
  appTypeSupportsPlayspace,
} from '../constants/appTypes.js';

/**
 * Determines app access permissions and identifies public viewers
 * @param {Object} app - The app object from API
 * @param {Object|null} user - The current user object (null for anonymous)
 * @param {Object|null} membership - The user's membership in the app (null if none)
 * @returns {Object} Access determination results
 */
export function determineAppAccess(app, user, membership) {
  if (!app) {
    return {
      canView: false,
      isOwner: false,
      isAdmin: false,
      isMember: false,
      isSiteAdmin: false,
      isPublicViewer: false,
      membership: null,
      error: 'no_app'
    };
  }

  const userId = user?._id || user?.id || user?.userId;
  const isSiteAdmin = user?.role === 'admin';

  // Determine roles based on membership
  const isOwner = membership?.role === 'owner' ||
    membership?.role === 'site_admin' ||  // Site admin membership = owner level
    (app.ownerId && userId && String(app.ownerId) === String(userId)) ||
    isSiteAdmin;
  const isAdmin = membership?.role === 'admin' || isSiteAdmin;
  const isMember = !!membership;

  // Determine access permissions
  const canView = app.isPublic || isOwner || isAdmin || isMember;

  // Identify public viewers (no membership but can view public app)
  const isPublicViewer = !membership && app.isPublic;

  return {
    canView,
    isOwner,
    isAdmin,
    isMember,
    isSiteAdmin,
    isPublicViewer,  // Key flag for view counting
    membership,
    error: null
  };
}

/**
 * Checks if a user should be counted as a view for analytics
 * @param {Object} access - Result from determineAppAccess()
 * @returns {boolean} Whether this access should increment view counters
 */
export function shouldCountAsView(access) {
  // Only count views from public viewers (non-members accessing public apps)
  return access.isPublicViewer === true;
}

/**
 * Determines if the workspace button should be visible for a user on an app.
 * Only admin, owner, and site-admin can see the workspace button.
 * Members and users with no account should not see it (nothing to do with app visibility).
 * @param {Object} access - Access object from determineAppAccess() (or { isOwner, isAdmin })
 * @returns {boolean} Whether the workspace button should be shown
 */
export function canShowWorkspaceButton(access) {
  if (!access) return false;
  return !!(access.isOwner || access.isAdmin);
}

/**
 * Whether the Workspace (IDE) entry point should be offered for this app in this environment.
 * Combines role-based gating (owner/admin) with app-type environment policy from the registry.
 * Does not check remote-only runtime state (service worker, cloud bundle, etc.).
 *
 * @param {Object} access - Access object from determineAppAccess() (or { isOwner, isAdmin })
 * @param {Object} app - The app object (must have appType)
 * @param {Object} options
 * @param {boolean} options.isDesktop - Whether we're running inside Revolt Desktop
 * @returns {boolean}
 */
export function canOpenWorkspace(access, app, { isDesktop = false } = {}) {
  if (!canShowWorkspaceButton(access)) return false;
  if (!app) return false;
  if (!appTypeSupportsWorkspace(app.appType)) return false;
  if (!canRunAppTypeInEnvironment(app.appType, { isDesktop })) return false;
  return true;
}

/**
 * Whether the Playspace (preview) entry point should be offered for this app in this environment.
 * Playspace visibility is permissive by default (public apps can be viewed by anyone),
 * but it is hidden for app types that don't support a preview or can't run here.
 *
 * @param {Object} app - The app object (must have appType)
 * @param {Object} options
 * @param {boolean} options.isDesktop - Whether we're running inside Revolt Desktop
 * @returns {boolean}
 */
export function canOpenPlayspace(app, { isDesktop = false } = {}) {
  if (!app) return false;
  if (!appTypeSupportsPlayspace(app.appType)) return false;
  if (!canRunAppTypeInEnvironment(app.appType, { isDesktop })) return false;
  return true;
}

/**
 * Return the full app-type definition for an app, or null if unknown.
 * Convenience re-export so components can get badge/icon data alongside access checks.
 * @param {Object} app
 * @returns {import('../constants/appTypes.js').AppTypeDefinition|null}
 */
export function getAppTypeDefinition(app) {
  return getAppType(app?.appType);
}

/**
 * Determines if the remix button should be visible for a user on an app.
 * - Owner, admin, site-admin: always show (regardless of isPublic or allowRemixing)
 * - Member and no-account: only show if app.allowRemixing is enabled
 * @param {Object} app - The app object (must have allowRemixing)
 * @param {Object} access - Access object from determineAppAccess() (or { isOwner, isAdmin })
 * @returns {boolean} Whether the remix button should be shown
 */
export function canShowRemixButton(app, access) {
  if (!app) return false;

  // Owner, admin, site-admin: always show remix button
  if (access?.isOwner || access?.isAdmin) {
    return true;
  }

  // Member and no-account: only if allowRemixing is enabled for the app
  return app.allowRemixing === true;
}